import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import ApiConstants from 'src/app/shared/constants/api.constants';
import { catchError } from 'rxjs/operators';
import moment from 'moment';
import { environment } from '../../../environments/environment';
import {
  CityResponse,
  CountryResponse,
  LanguageResponse,
  RegionResponse,
} from '../model/common.model';
import { USER_TYPES } from '../constants/common.constants';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  static getQuerySingleParams(name: string, value: string | boolean | number) {
    let str = '&';
    str += `${name}=${value}&`;
    return str.substring(0, str.length - 1);
  }

  static getQueryArrayParams(name: string, values: number[]) {
    let str = '?';
    values.forEach((ele) => {
      str += `${name}=${ele}&`;
    });
    return str.substring(0, str.length - 1);
  }

  static downloadFile(url: string, fileName: string) {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  static formatDate(dateString: string): string {
    return moment(dateString).format('YYYY-MM-DD');
  }

  static formatDuration(minutes: number): string {
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const remainingSecondsAfterHours = totalSeconds % 3600;
    const mins = Math.floor(remainingSecondsAfterHours / 60);
    const secs = remainingSecondsAfterHours % 60;
    let formattedDuration = '';

    if (hours > 0) {
      formattedDuration += `${hours} hr `;
    }

    if (mins > 0) {
      formattedDuration += `${mins} min `;
    }

    if (secs > 0) {
      formattedDuration += `${secs} sec`;
    }

    return formattedDuration.trim();
  }

  static getFormattedDate(utcDate: string): string {
    return moment.utc(utcDate).local().format('DD-MMM-YYYY hh:mm A');
  }

  static joinArrayStrings<T>(itemArray: T[] | null | undefined, propertyName: keyof T): string {
    if (!itemArray || itemArray.length === 0) {
      return '';
    }

    return itemArray.map((item) => item[propertyName]).join(', ');
  }

  static getFormattedDateTime(utcDate: string): string {
    return moment.utc(utcDate).local().format('DD-MMM-YYYY hh:mm A');
  }

  getCountry(appUserType: string): Observable<CountryResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.COUNTRY_PHONE_CODE}`;

    if (appUserType !== USER_TYPES.ADMIN && appUserType !== '') {
      API_URL += `?appUserType=${appUserType}`;
    }

    return this.http
      .get<CountryResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getLanguage(): Observable<LanguageResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.LANGUAGES}`;
    return this.http
      .get<LanguageResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getRegions(
    countryIds: { id: number; countryName: string }[] | number[],
  ): Observable<RegionResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.REGIONS}`;

    if (Array.isArray(countryIds) && countryIds.length > 0 && countryIds[0] !== null) {
      const ids = countryIds.map((country) => (typeof country === 'number' ? country : country.id));
      API_URL += CommonService.getQueryArrayParams('countryIds', ids);
    }

    return this.http
      .get<RegionResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCities(regionIds: number[]): Observable<CityResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.CITES}`;

    if (Array.isArray(regionIds) && regionIds.length > 0) {
      API_URL += CommonService.getQueryArrayParams('regionIds', regionIds);
    }

    return this.http
      .get<CityResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
